export const HEATED_SERVICES_NAME = 'HEATED SERVICES';
export const FSC_HEATED_SERVICE_NAME = 'FSC HEATED SERVICE';
export const FSC_HEATED_HELPER_TEXT = 'Requires Heated Services';
export const FSC_HEATED_REQUIRES_HEATED_MESSAGE =
  'FSC Heated Service requires Heated Services to be selected';
export const FSC_HEATED_TARIFF_NOTE =
  'Uses weekly fuel surcharge % of Heated Services total';

export function normalizeAccessorialName(name) {
  return (name || '').trim().toLowerCase();
}

function getAccessorialName(accessorialOrName) {
  if (typeof accessorialOrName === 'string') return accessorialOrName;
  return accessorialOrName?.name || accessorialOrName?.title || '';
}

export function isHeatedServices(accessorialOrName) {
  return (
    normalizeAccessorialName(getAccessorialName(accessorialOrName)) ===
    normalizeAccessorialName(HEATED_SERVICES_NAME)
  );
}

export function isFscHeatedService(accessorialOrName) {
  return (
    normalizeAccessorialName(getAccessorialName(accessorialOrName)) ===
    normalizeAccessorialName(FSC_HEATED_SERVICE_NAME)
  );
}

export function hasHeatedServices(accessorials = []) {
  return (accessorials || []).some((acc) => isHeatedServices(acc));
}

export function findHeatedServices(catalog = []) {
  return (catalog || []).find((acc) => isHeatedServices(acc)) || null;
}

export function findFscHeatedService(catalog = []) {
  return (catalog || []).find((acc) => isFscHeatedService(acc)) || null;
}

export function hasHeatedServicesByUuids(selectedUuids = [], catalog = []) {
  const heated = findHeatedServices(catalog);
  return Boolean(heated && (selectedUuids || []).includes(heated.uuid));
}

export function isFscHeatedDisabled(selectedAccessorials = []) {
  return !hasHeatedServices(selectedAccessorials);
}

export function isFscHeatedDisabledByUuids(selectedUuids = [], catalog = []) {
  return !hasHeatedServicesByUuids(selectedUuids, catalog);
}

/** Drop FSC HEATED SERVICE when HEATED SERVICES is not in the selection. */
export function stripFscHeatedWithoutHeated(accessorials = []) {
  if (hasHeatedServices(accessorials)) return accessorials || [];
  return (accessorials || []).filter((acc) => !isFscHeatedService(acc));
}

/**
 * Keep UUID selections consistent with the heated → FSC heated dependency.
 * - Removing Heated Services also removes FSC Heated Service.
 * - Adding FSC without Heated: auto-adds Heated when autoAddHeated, else strips FSC.
 */
export function applyAccessorialUuidSelectionChange({
  prevUuids = [],
  nextUuids = [],
  catalog = [],
  autoAddHeated = false,
}) {
  const heated = findHeatedServices(catalog);
  const fsc = findFscHeatedService(catalog);
  if (!fsc) return nextUuids;

  let result = [...(nextUuids || [])];

  if (
    heated &&
    prevUuids.includes(heated.uuid) &&
    !result.includes(heated.uuid)
  ) {
    result = result.filter((uuid) => uuid !== fsc.uuid);
  }

  if (result.includes(fsc.uuid) && heated && !result.includes(heated.uuid)) {
    if (autoAddHeated) {
      result = [...result, heated.uuid];
    } else {
      result = result.filter((uuid) => uuid !== fsc.uuid);
    }
  }

  return result;
}

/** Ensure address defaults that include FSC also include Heated Services. */
export function ensureHeatedWithFscDefaults(selectedUuids = [], catalog = []) {
  return applyAccessorialUuidSelectionChange({
    prevUuids: selectedUuids,
    nextUuids: selectedUuids,
    catalog,
    autoAddHeated: true,
  });
}

/**
 * If default accessorial objects include FSC HEATED SERVICE but not HEATED SERVICES,
 * append Heated Services from the catalog (when available).
 */
export function enrichAccessorialsWithHeatedDependency(
  accessorials = [],
  catalog = [],
) {
  if (!hasHeatedServices(accessorials) && accessorials.some(isFscHeatedService)) {
    const heated = findHeatedServices(catalog);
    if (heated && !accessorials.some((acc) => acc.uuid === heated.uuid)) {
      return [...accessorials, heated];
    }
  }
  return accessorials;
}
